import {
    Box,
    Grid,
    Paper,
    Typography,
    useTheme,
    alpha,
    Skeleton
} from '@mui/material';
import React from 'react';
import { formatCurrency, formatNumber } from '../../../commonFunctions/CommonFunctions';

export interface StatsCardProps {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    trend?: number;
    subtitle?: string;
    loading?: boolean;
    title?: string;
    format?: "currency" | "number" | "text";
    currencySymbol?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
    icon,
    value,
    label,
    color = 'primary',
    trend,
    subtitle,
    title,
    format = "text",
    currencySymbol = '₹',
    loading
}) => {

    const theme = useTheme();
    
    const displayValue = (() => {
        if (format === "currency") {
            return formatCurrency(value, currencySymbol);
        }

        if (format === "number") {
            return formatNumber(value);
        }

        return value;
    })();

    return (
        <Grid item xs={12} sm={6} md={3}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(
                        theme.palette[color].main,
                        0.08
                    )} 0%, ${alpha(theme.palette[color].main, 0.02)} 100%)`,
                    border: `1px solid ${alpha(theme.palette[color].main, 0.15)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 24px -8px ${alpha(theme.palette[color].main, 0.3)}`,
                        '& .icon-container': {
                            transform: 'scale(1.1) rotate(5deg)',
                            backgroundColor: alpha(theme.palette[color].main, 0.15),
                        },
                        '& .decorative-circle': {
                            transform: 'scale(1.2)',
                            opacity: 0.1,
                        },
                    },
                }}
            >
                {loading ? (
                    // Skeleton loading state
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Skeleton
                            variant="circular"
                            width={48}
                            height={48}
                            sx={{
                                mb: 2,
                                bgcolor: alpha(theme.palette[color].main, 0.1),
                            }}
                        />
                        <Skeleton
                            variant="text"
                            width="70%"
                            height={40}
                            sx={{
                                bgcolor: alpha(theme.palette[color].main, 0.08),
                            }}
                        />
                        <Skeleton
                            variant="text"
                            width="50%"
                            height={24}
                            sx={{
                                bgcolor: alpha(theme.palette[color].main, 0.06),
                            }}
                        />
                        <Skeleton
                            variant="text"
                            width="40%"
                            height={20}
                            sx={{
                                bgcolor: alpha(theme.palette[color].main, 0.04),
                            }}
                        />
                    </Box>
                ) : (
                    // Content
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Header with title and icon */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                            {title && (
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: theme.palette[color].main,
                                        fontWeight: 600,
                                        letterSpacing: 0.5,
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {title}
                                </Typography>
                            )}

                            {/* Icon */}
                            <Box
                                className="icon-container"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 48,
                                    height: 48,
                                    borderRadius: 3,
                                    backgroundColor: alpha(theme.palette[color].main, 0.1),
                                    color: theme.palette[color].main,
                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                }}
                            >
                                {icon}
                            </Box>
                        </Box>

                        {/* Value and Trend */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    color: theme.palette[color].dark || theme.palette[color].main,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {displayValue}
                            </Typography>

                            {typeof trend === 'number' && trend !== 0 && (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 2,
                                        backgroundColor: alpha(
                                            trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                                            0.1
                                        ),
                                        color: trend > 0 ? 'success.main' : 'error.main',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.2,
                                        }}
                                    >
                                        {trend > 0 ? '▲' : '▼'} {Math.abs(trend)}%
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Label */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                                mb: 0.5,
                            }}
                        >
                            {label}
                        </Typography>

                        {/* Subtitle */}
                        {subtitle && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.disabled',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    '&::before': {
                                        content: '"•"',
                                        color: theme.palette[color].main,
                                        fontSize: '1.2rem',
                                        lineHeight: 0,
                                    },
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Decorative elements */}
                <Box
                    className="decorative-circle"
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(
                            theme.palette[color].main,
                            0.08
                        )} 0%, transparent 70%)`,
                        transition: 'transform 0.5s ease, opacity 0.5s ease',
                        zIndex: 0,
                    }}
                />

                {/* Small accent dots */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        left: 10,
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette[color].main, 0.2),
                        boxShadow: `0 0 0 2px ${alpha(theme.palette[color].main, 0.1)}`,
                    }}
                />
            </Paper>
        </Grid>
    );
};

export default StatsCard;